// One place that turns a date into text. Reads settings.dateFormat.
//
// Why dates are parsed by hand: new Date('2026-08-14') is parsed as UTC, so in a
// negative-offset timezone it renders as 13 Aug. Every date column in this app is a
// plain calendar date with no time, so it is split and rebuilt in local time.
import useStore from '../store/useStore';

const MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MON_FULL = ['January','February','March','April','May','June','July',
                  'August','September','October','November','December'];
const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

export const DATE_FORMATS = [
  { value: 'DD/MM/YYYY',  label: 'DD/MM/YYYY',  sample: '14/08/2026' },
  { value: 'MM/DD/YYYY',  label: 'MM/DD/YYYY',  sample: '08/14/2026' },
  { value: 'DD-MMM-YYYY', label: 'DD-MMM-YYYY', sample: '14-Aug-2026' },
  { value: 'YYYY-MM-DD',  label: 'YYYY-MM-DD',  sample: '2026-08-14' },
];

// Accepts an ISO string, a Date, or a timestamp. Returns null when unparseable.
function parts(value) {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return { y: value.getFullYear(), m: value.getMonth(), d: value.getDate(), dow: value.getDay() };
  }
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return { y: Number(m[1]), m: Number(m[2]) - 1, d: Number(m[3]), dow: dt.getDay() };
  }
  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return null;
  return { y: dt.getFullYear(), m: dt.getMonth(), d: dt.getDate(), dow: dt.getDay() };
}

function currentFormat() {
  try {
    const f = useStore.getState()?.settings?.dateFormat;
    return DATE_FORMATS.some(x => x.value === f) ? f : 'DD/MM/YYYY';
  } catch { return 'DD/MM/YYYY'; }
}

const pad = (n) => String(n).padStart(2, '0');

// formatDate('2026-08-14') -> '14/08/2026'
// Unparseable input is returned unchanged, so a bad row shows its raw value
// instead of the word Invalid.
export function formatDate(value, fmtOverride) {
  const p = parts(value);
  if (!p) return value == null ? '' : String(value);
  const fmt = fmtOverride || currentFormat();
  switch (fmt) {
    case 'MM/DD/YYYY':  return `${pad(p.m + 1)}/${pad(p.d)}/${p.y}`;
    case 'DD-MMM-YYYY': return `${pad(p.d)}-${MON[p.m]}-${p.y}`;
    case 'YYYY-MM-DD':  return `${p.y}-${pad(p.m + 1)}-${pad(p.d)}`;
    default:            return `${pad(p.d)}/${pad(p.m + 1)}/${p.y}`;
  }
}

// formatDateLong('2026-08-14') -> 'Friday, 14 August 2026'. For page headers.
// Word order follows the chosen format: month-first formats read 'August 14, 2026'.
export function formatDateLong(value, opts = {}) {
  const p = parts(value);
  if (!p) return value == null ? '' : String(value);
  const monthFirst = currentFormat() === 'MM/DD/YYYY';
  const body = monthFirst
    ? `${MON_FULL[p.m]} ${p.d}, ${p.y}`
    : `${p.d} ${MON_FULL[p.m]} ${p.y}`;
  return opts.weekday === false ? body : `${DAYS[p.dow]}, ${body}`;
}

// 'YYYY-MM-DD' for a Date, for database writes and <input type="date">.
export function toISODate(value) {
  const p = parts(value);
  return p ? `${p.y}-${pad(p.m + 1)}-${pad(p.d)}` : '';
}

// formatDayMonth('2026-08-14') -> '14/08' (no year, same order and separator as the chosen format).
export function formatDayMonth(value) {
  const p = parts(value);
  if (!p) return value == null ? '' : String(value);
  switch (currentFormat()) {
    case 'MM/DD/YYYY':  return `${pad(p.m + 1)}/${pad(p.d)}`;
    case 'DD-MMM-YYYY': return `${pad(p.d)}-${MON[p.m]}`;
    case 'YYYY-MM-DD':  return `${pad(p.m + 1)}-${pad(p.d)}`;
    default:            return `${pad(p.d)}/${pad(p.m + 1)}`;
  }
}

// formatTime(date) -> '14:05' (24-hour, local time).
export function formatTime(value) {
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  return `${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

// formatDateTime('2026-08-14T09:30:00Z') -> '14/08/2026 09:30' in the chosen date format.
// A date-only string has no time, so only the date is returned.
export function formatDateTime(value) {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDate(value);
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return `${formatDate(dt)} ${formatTime(dt)}`;
}
